import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"

const RouterContext = createContext(null)

export function RouterProvider({ children }) {
  const [path, setPath] = useState(() => window.location.pathname)

  useEffect(() => {
    const handlePopState = () => setPath(window.location.pathname)
    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [])

  const navigate = useCallback((to, { replace = false } = {}) => {
    window.history[replace ? "replaceState" : "pushState"]({}, "", to)
    setPath(window.location.pathname)
    window.scrollTo({ top: 0, behavior: "instant" })
  }, [])

  const value = useMemo(() => ({ path, navigate }), [navigate, path])
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
}

export function Link({ to, children, className = "", ...props }) {
  const { navigate } = useContext(RouterContext)
  function follow(event) {
    props.onClick?.(event)
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    navigate(to)
  }
  return <a {...props} className={className} href={to} onClick={follow}>{children}</a>
}

export function NavLink({ to, end = false, children, className = "", ...props }) {
  const { path } = useContext(RouterContext)
  const isActive = end ? path === to : path === to || path.startsWith(`${to}/`)
  const resolvedClassName = typeof className === "function" ? className({ isActive }) : className
  return <Link {...props} className={resolvedClassName} to={to}>{children}</Link>
}

export function usePath() {
  return useContext(RouterContext).path
}

export function useNavigate() {
  return useContext(RouterContext).navigate
}

export function useParams(pattern) {
  const path = usePath()
  const names = []
  const expression = new RegExp(`^${pattern.replace(/:([^/]+)/g, (_, name) => { names.push(name); return "([^/]+)" })}/?$`)
  const match = path.match(expression)
  return match ? Object.fromEntries(names.map((name, index) => [name, decodeURIComponent(match[index + 1])])) : {}
}
